import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import {
  Text,
  Badge,
  Loader,
  Card,
  Group,
  ScrollArea,
} from '@mantine/core';
import Package from '../components/Package';
import { Calendar, ChevronDown, ChevronUp, Download, Search } from 'lucide-react';
import { Container, Title, Table, Grid, Box } from '@mantine/core';
import { supabase } from '../supabase/client';
import { formatDate } from '../utils';


const fetchOrders = async (userId: string) => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('profile_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

export default function OrderHistory() {
  const { user } = useAuth();
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: orders, isLoading:loading, isError } = useQuery({
    queryKey: ['order-history', user?.id],
    queryFn: () => fetchOrders(user?.id ?? ''),
    enabled: !!user?.id,
  });

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Filter orders based on search term and status filter
  const filteredOrders = orders?.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (Array.isArray(order.products) && order.products?.some((product: any) => 
                           product.product_name?.toLowerCase().includes(searchTerm.toLowerCase())));
    
    const matchesStatus = statusFilter === "all" || order.payment_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return loading ? (
      <Group justify="center">
        <Loader />
      </Group>
    ) : (
      <>
        <div className="min-h-screen to-white from-stone-50 bg-linear-to-b">
          {/* Header */}
          <header className="bg-white border-b border-gray-200">
            <div className="container mx-auto px-4 py-6">
              <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Order History</h1>
                {/* <button className="px-4 py-2 bg-indigo-600 text-white rounded-md flex items-center gap-2 hover:bg-indigo-700 transition-colors">
                  <Download size={18} />
                  Export
                </button> */}
              </div>
              <p className="text-gray-500 mt-1">View and manage your practice test purchases</p>
            </div>
          </header>
    
          {/* Filters */}
          <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by order ID or product name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="min-w-40">
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all appearance-none bg-white"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                  </div>
                  {/* <div className="min-w-40">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Date range"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                      />
                      <Calendar className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
    
          {/* Order List */}
          <div className="container mx-auto px-4 pb-12">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 bg-gray-50 p-4 border-b border-gray-200 text-sm font-medium text-gray-500">
                <div className="col-span-4 md:col-span-3">Date</div>
                <div className="col-span-3 md:col-span-3">Total</div>
                <div className="hidden md:block md:col-span-3">Status</div>
                <div className="col-span-3 md:col-span-2 text-right">Actions</div>
              </div>
    
              {/* Order Rows */}
              {(filteredOrders ?? []).length > 0 ? (
                filteredOrders?.map((order) => (
                  <div key={order.id} className="border-b border-gray-200 last:border-0">
                    {/* Order Summary Row */}
                    <div 
                      className="grid grid-cols-12 p-4 items-center hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => toggleOrderExpansion(order.id)}
                    >
                      <div className="col-span-4 md:col-span-3 text-gray-500">{order.created_at && formatDate(order.created_at)}</div>
                      <div className="col-span-3 md:col-span-3 font-medium">${((order.amount_total ?? 0) / 100).toFixed(2)}</div>
                      <div className="hidden md:block md:col-span-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {`${order.payment_status?.charAt(0).toUpperCase()}${order.payment_status?.slice(1)}`}
                        </span>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <button className="p-1 rounded hover:bg-gray-100 transition-colors">
                          {expandedOrder === order.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                    </div>
    
                    {/* Expanded Order Details */}

                    {expandedOrder === order.id && (
                      <div className="bg-gray-50 p-4 border-t border-gray-200">
                        <h3 className="font-medium text-gray-900 mb-3">Products</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {Array.isArray(order.products) && order.products?.map((product: any, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{product.product_name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product.quantity}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">${(product.amount_total / 100).toFixed(2)}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">${((product.amount_total / 100) * product.quantity).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                              <tr>
                                <td colSpan={3} className="px-4 py-3 text-sm font-medium text-gray-500 text-right">Total:</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">${((order.amount_total ?? 0) / 100).toFixed(2)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        
                        <div className="mt-4 flex justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Payment ID#</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {order.id}
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Payment Details</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {order.payment_status === 'paid' ? 'Paid via Credit Card' : 'Payment Pending'}
                            </p>
                          </div>
                          <div>
                            {/* <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors">
                              View Receipt
                            </button> */}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p>No orders match your search criteria.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )
}
